import { z } from "zod";

import { DirectoryEmailSchema } from "./workspace-user-schemas";
import { OpaqueIdSchema } from "./schemas";

const OptionalText = (maximum: number) =>
  z.string().trim().min(1).max(maximum).nullable().default(null);
const TimestampSchema = z.number().int().positive();
const JsonObjectSchema = z
  .record(z.string().min(1).max(100), z.unknown())
  .refine((value) => JSON.stringify(value).length <= 16_384, "Context metadata is too large.");

export const CustomerProfileInputSchema = z.strictObject({
  displayName: z.string().trim().min(2).max(160),
  summary: OptionalText(4_000),
});

export const CustomerProfileSchema = CustomerProfileInputSchema.extend({
  organizationId: OpaqueIdSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export const CustomerProfileResponseSchema = z.strictObject({ profile: CustomerProfileSchema });

export const CustomerPersonInputSchema = z.strictObject({
  displayName: z.string().trim().min(1).max(160),
  title: OptionalText(160),
  email: DirectoryEmailSchema.nullable().default(null),
  phone: OptionalText(80),
  notes: OptionalText(4_000),
  status: z.enum(["active", "inactive"]).default("active"),
});
export const CustomerPersonSchema = CustomerPersonInputSchema.extend({
  id: OpaqueIdSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const CustomerToolInputSchema = z.strictObject({
  platformToolId: OpaqueIdSchema.nullable().default(null),
  displayName: z.string().trim().min(1).max(160),
  description: OptionalText(4_000),
  status: z.enum(["planned", "active", "retired"]).default("planned"),
});
export const CustomerToolSchema = CustomerToolInputSchema.extend({
  id: OpaqueIdSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const CustomerDealInputSchema = z.strictObject({
  externalSystem: OptionalText(80),
  externalId: OptionalText(255),
  displayName: z.string().trim().min(1).max(240),
  status: OptionalText(80),
  notes: OptionalText(4_000),
});
export const CustomerDealSchema = CustomerDealInputSchema.extend({
  id: OpaqueIdSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const CustomerContextItemInputSchema = z.strictObject({
  kind: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(240),
  bodyText: OptionalText(20_000),
  toolId: OpaqueIdSchema.nullable().optional(),
  metadata: JsonObjectSchema.default({}),
  sourceCreatedAt: TimestampSchema.nullable().default(null),
});
export const CustomerContextItemSchema = CustomerContextItemInputSchema.extend({
  id: OpaqueIdSchema,
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});

export const CustomerContextSummarySchema = z.strictObject({
  profile: CustomerProfileSchema.nullable(),
  people: z.array(CustomerPersonSchema).max(200),
  tools: z.array(CustomerToolSchema).max(200),
  deals: z.array(CustomerDealSchema).max(200),
  contextItems: z.array(CustomerContextItemSchema).max(200),
});

export const InternalCustomerRecordCreateSchema = z.discriminatedUnion("collection", [
  z.strictObject({
    collection: z.literal("people"),
    id: OpaqueIdSchema,
    record: CustomerPersonInputSchema,
  }),
  z.strictObject({
    collection: z.literal("deals"),
    id: OpaqueIdSchema,
    record: CustomerDealInputSchema,
  }),
  z.strictObject({
    collection: z.literal("context-items"),
    id: OpaqueIdSchema,
    record: CustomerContextItemInputSchema,
  }),
]);

export const CustomerRecordCreateResponseSchema = z.discriminatedUnion("collection", [
  z.strictObject({ collection: z.literal("people"), record: CustomerPersonSchema }),
  z.strictObject({ collection: z.literal("deals"), record: CustomerDealSchema }),
  z.strictObject({ collection: z.literal("context-items"), record: CustomerContextItemSchema }),
]);

export type CustomerProfileInput = z.infer<typeof CustomerProfileInputSchema>;
export type CustomerPersonInput = z.infer<typeof CustomerPersonInputSchema>;
export type CustomerToolInput = z.infer<typeof CustomerToolInputSchema>;
export type CustomerTool = z.infer<typeof CustomerToolSchema>;
export type CustomerDealInput = z.infer<typeof CustomerDealInputSchema>;
export type CustomerContextItemInput = z.infer<typeof CustomerContextItemInputSchema>;
export type CustomerContextItem = z.infer<typeof CustomerContextItemSchema>;
export type CustomerContextSummary = z.infer<typeof CustomerContextSummarySchema>;

export const CustomerNoteUpdateSchema = z.strictObject({
  toolId: OpaqueIdSchema.nullable(),
  expected: z.strictObject({
    title: z.string().min(1).max(240),
    bodyText: z.string().max(20_000).nullable(),
    updatedAt: TimestampSchema,
  }),
  title: z.string().trim().min(1).max(240),
  bodyText: z.string().max(20_000).nullable(),
});
export const CustomerNoteResponseSchema = z.strictObject({ note: CustomerContextItemSchema });

// ADR-0088: which context person is the customer's primary contact.
export const CustomerPrimaryContactSchema = z.strictObject({ personId: OpaqueIdSchema.nullable() });
